
import { useParams, useNavigate } from 'react-router-dom';
import { useBilling } from './hooks/useBilling';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

export default function InvoiceDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { invoiceById, markPaid, createInvoice, updateInvoice } = useBilling();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isEditing, setIsEditing] = useState(id === 'new');
  
  const { register, handleSubmit, watch, setValue, formState: { isSubmitting } } = useForm();
  const paymentForm = useForm();

  const query = invoiceById(id);

  if (id !== 'new' && query.isLoading) {
    return <div className="p-6">Loading invoice...</div>;
  }

  const invoice = query.data;
  const isNewInvoice = id === 'new';

  const calculateTotals = (items: any[]) => {
    const subtotal = items.reduce((sum, item) => sum + (item.weight * item.rate), 0);
    const makingCharges = items.reduce((sum, item) => sum + item.makingCharge, 0);
    const gstAmount = items.reduce((sum, item) => sum + ((item.weight * item.rate + item.makingCharge) * item.gstRate / 100), 0);
    const total = subtotal + makingCharges + gstAmount;
    
    return { subtotal, makingCharges, gst: gstAmount, total };
  };

  const onSubmitInvoice = async (data: any) => {
    try {
      const items = data.items || [];
      const totals = calculateTotals(items);
      
      const invoiceData = {
        ...data,
        ...totals,
        status: 'draft'
      };

      if (isNewInvoice) {
        await createInvoice.mutateAsync(invoiceData);
        navigate('/billing/invoices');
      } else {
        await updateInvoice.mutateAsync({ id, data: invoiceData });
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Failed to save invoice:', error);
    }
  };

  const onMarkPaid = async (paymentData: any) => {
    try {
      await markPaid.mutateAsync({
        id,
        paymentData: {
          ...paymentData,
          amount: parseFloat(paymentData.amount),
          paymentDate: new Date().toISOString()
        }
      });
      setShowPaymentModal(false);
    } catch (error) {
      console.error('Failed to mark as paid:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'badge pending',
      sent: 'badge pending',
      paid: 'badge approved',
      overdue: 'badge suspended',
      cancelled: 'badge suspended'
    };
    return badges[status as keyof typeof badges] || 'badge pending';
  };

  if (isEditing || isNewInvoice) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-[var(--color-primary)]">
            {isNewInvoice ? 'Create New Invoice' : 'Edit Invoice'}
          </h1>
          <div className="flex gap-2">
            {!isNewInvoice && (
              <button
                onClick={() => setIsEditing(false)}
                className="btn-danger"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmitInvoice)} className="space-y-6">
          {/* Invoice Header */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Invoice Details</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Invoice Number</label>
                <input
                  className="input"
                  {...register('invoiceNo', { required: true })}
                  defaultValue={invoice?.invoiceNo || `INV-${Date.now()}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Customer Name</label>
                <input
                  className="input"
                  {...register('customer', { required: true })}
                  defaultValue={invoice?.customer || ''}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Invoice Date</label>
                <input
                  className="input"
                  type="date"
                  {...register('date', { required: true })}
                  defaultValue={invoice?.date || new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Due Date</label>
                <input
                  className="input"
                  type="date"
                  {...register('dueDate', { required: true })}
                  defaultValue={invoice?.dueDate || ''}
                />
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Customer Details</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  className="input"
                  type="email"
                  {...register('customerDetails.email')}
                  defaultValue={invoice?.customerDetails?.email || ''}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  className="input"
                  {...register('customerDetails.phone')}
                  defaultValue={invoice?.customerDetails?.phone || ''}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Address</label>
                <textarea
                  className="input"
                  rows={3}
                  {...register('customerDetails.address')}
                  defaultValue={invoice?.customerDetails?.address || ''}
                />
              </div>
            </div>
          </div>

          {/* Invoice Items */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Invoice Items</h2>
            <div className="space-y-4">
              {/* Items will be dynamically added here */}
              <div className="grid grid-cols-6 gap-2 text-sm font-medium">
                <div>Description</div>
                <div>Weight (g)</div>
                <div>Rate/g (₹)</div>
                <div>Making Charge (₹)</div>
                <div>GST %</div>
                <div>Amount (₹)</div>
              </div>
              
              {/* Sample item row */}
              <div className="grid grid-cols-6 gap-2">
                <input className="input" placeholder="Gold Ring" {...register('items.0.description')} />
                <input className="input" type="number" step="0.01" {...register('items.0.weight')} />
                <input className="input" type="number" step="0.01" {...register('items.0.rate')} />
                <input className="input" type="number" step="0.01" {...register('items.0.makingCharge')} />
                <input className="input" type="number" step="0.01" {...register('items.0.gstRate')} />
                <input className="input" type="number" step="0.01" readOnly />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Notes</h2>
            <textarea
              className="input"
              rows={3}
              placeholder="Additional notes or terms..."
              {...register('notes')}
              defaultValue={invoice?.notes || ''}
            />
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary"
            >
              {isSubmitting ? 'Saving...' : (isNewInvoice ? 'Create Invoice' : 'Update Invoice')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/billing/invoices')}
              className="btn-danger"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (!invoice) {
    return <div className="p-6">Invoice not found</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-[var(--color-primary)]">
          Invoice {invoice.invoiceNo}
        </h1>
        <div className="flex gap-2">
          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="btn-primary"
              >
                <i className="ri-edit-line mr-2"></i>
                Edit
              </button>
              <button
                onClick={() => setShowPaymentModal(true)}
                className="btn-primary"
              >
                <i className="ri-money-dollar-circle-line mr-2"></i>
                Mark as Paid
              </button>
            </>
          )}
          <button className="btn-primary">
            <i className="ri-download-line mr-2"></i>
            Download PDF
          </button>
        </div>
      </div>

      {/* Invoice Header */}
      <div className="card">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-lg font-semibold mb-4">Invoice Details</h2>
            <div className="space-y-2">
              <div><span className="font-medium">Invoice #:</span> {invoice.invoiceNo}</div>
              <div><span className="font-medium">Date:</span> {new Date(invoice.date).toLocaleDateString()}</div>
              <div><span className="font-medium">Due Date:</span> {new Date(invoice.dueDate).toLocaleDateString()}</div>
              <div>
                <span className="font-medium">Status:</span>
                <span className={`ml-2 ${getStatusBadge(invoice.status)}`}>
                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                </span>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-lg font-semibold mb-4">Customer Details</h2>
            <div className="space-y-2">
              <div><span className="font-medium">Name:</span> {invoice.customer}</div>
              <div><span className="font-medium">Email:</span> {invoice.customerDetails?.email}</div>
              <div><span className="font-medium">Phone:</span> {invoice.customerDetails?.phone}</div>
              <div><span className="font-medium">Address:</span> {invoice.customerDetails?.address}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Items */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Invoice Items</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--color-accent)]/20">
              <tr>
                <th className="th">Description</th>
                <th className="th">Weight (g)</th>
                <th className="th">Rate/g (₹)</th>
                <th className="th">Making Charge (₹)</th>
                <th className="th">GST %</th>
                <th className="th">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items?.map((item: any, index: number) => (
                <tr key={index} className="tr">
                  <td className="td">{item.description}</td>
                  <td className="td">{item.weight}</td>
                  <td className="td">₹{item.rate.toLocaleString()}</td>
                  <td className="td">₹{item.makingCharge.toLocaleString()}</td>
                  <td className="td">{item.gstRate}%</td>
                  <td className="td">₹{item.amount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Totals */}
      <div className="card">
        <div className="max-w-md ml-auto space-y-2">
          <div className="flex justify-between">
            <span>Subtotal:</span>
            <span>₹{invoice.subtotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span>Making Charges:</span>
            <span>₹{invoice.makingCharges.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span>GST:</span>
            <span>₹{invoice.gst.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-lg font-semibold border-t pt-2">
            <span>Total:</span>
            <span>₹{invoice.total.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Notes</h2>
          <p className="text-gray-700">{invoice.notes}</p>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Record Payment</h2>
            <form onSubmit={paymentForm.handleSubmit(onMarkPaid)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Payment Amount</label>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  defaultValue={invoice.total}
                  {...paymentForm.register('amount', { required: true })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Payment Method</label>
                <select className="input pr-8" {...paymentForm.register('paymentMethod', { required: true })}>
                  <option value="">Select Method</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Transaction ID (Optional)</label>
                <input
                  className="input"
                  {...paymentForm.register('transactionId')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                <textarea
                  className="input"
                  rows={3}
                  {...paymentForm.register('notes')}
                />
              </div>
              
              <div className="flex gap-2">
                <button type="submit" className="btn-primary flex-1">
                  Record Payment
                </button>
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="btn-danger"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
